import React from 'react';
import { Link } from 'react-router-dom';
import { FaEye, FaMoneyBillWave, FaShoppingCart } from 'react-icons/fa';
import Button from '../../common/Button';
import CuentaCorrienteEstadoBadge from './CuentaCorrienteEstadoBadge';
import { CRM_ANTIGUEDAD, resolverAntiguedadVisualCrm } from '../../../utils/crmCuentaCorriente';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0);

const formatFecha = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR');
  } catch {
    return '—';
  }
};

const SALDO_CLASE = {
  [CRM_ANTIGUEDAD.CRITICO]: 'text-red-300 font-bold text-base tabular-nums',
  [CRM_ANTIGUEDAD.ALERTA]: 'text-orange-300 font-bold text-base tabular-nums',
  [CRM_ANTIGUEDAD.TEMPRANO]: 'text-amber-200 font-semibold text-base tabular-nums',
  [CRM_ANTIGUEDAD.AL_DIA]: 'text-e0e3-muted text-sm',
  [CRM_ANTIGUEDAD.NEUTRO]: 'text-e0e3-muted text-sm'
};

const FILA_CLASE = {
  [CRM_ANTIGUEDAD.CRITICO]: 'bg-red-500/[0.07] hover:bg-red-500/[0.1]',
  [CRM_ANTIGUEDAD.ALERTA]: 'bg-orange-500/[0.05] hover:bg-orange-500/[0.08]',
  [CRM_ANTIGUEDAD.TEMPRANO]: 'bg-amber-500/[0.03] hover:bg-amber-500/[0.06]',
  [CRM_ANTIGUEDAD.AL_DIA]: 'hover:bg-white/[0.02]',
  [CRM_ANTIGUEDAD.NEUTRO]: 'hover:bg-white/[0.02]'
};

const CuentaCorrienteTabla = ({ filas, onRegistrarPago, pagoLoadingId }) => {
  if (!filas.length) {
    return (
      <div className="rounded-lg border border-e0e3-border/25 bg-e0e3-surface/20 px-4 py-10 text-center text-sm text-e0e3-muted">
        No hay clientes que coincidan con los filtros seleccionados.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-e0e3-border/25">
      <table className="min-w-full divide-y divide-e0e3-border/20 text-sm">
        <thead className="bg-white/[0.03]">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-e0e3-muted">Cliente</th>
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-e0e3-muted">Saldo</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-e0e3-muted hidden md:table-cell">Última compra</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-e0e3-muted hidden lg:table-cell">Último pago</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-e0e3-muted">Estado</th>
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-e0e3-muted">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-e0e3-border/15">
          {filas.map((fila) => {
            const antiguedad = resolverAntiguedadVisualCrm(fila);
            const rowClass = FILA_CLASE[antiguedad] || FILA_CLASE[CRM_ANTIGUEDAD.NEUTRO];
            const saldoClass = fila.saldo > 0
              ? (SALDO_CLASE[antiguedad] || SALDO_CLASE[CRM_ANTIGUEDAD.TEMPRANO])
              : SALDO_CLASE[CRM_ANTIGUEDAD.NEUTRO];
            const pagoCargando = pagoLoadingId === fila.id;

            return (
              <tr key={fila.id} className={`transition-colors ${rowClass}`}>
                <td className="px-4 py-3 min-w-[180px]">
                  <p className="font-medium text-e0e3-foreground truncate" title={fila.nombre}>
                    {fila.nombre}
                  </p>
                  {fila.telefono ? (
                    <p className="text-xs text-e0e3-muted mt-0.5">{fila.telefono}</p>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <span className={saldoClass}>
                    {fila.saldo > 0 ? formatMoney(fila.saldo) : '—'}
                  </span>
                </td>
                <td className="px-4 py-3 text-e0e3-muted hidden md:table-cell whitespace-nowrap">
                  {formatFecha(fila.ultimaCompra)}
                </td>
                <td className="px-4 py-3 text-e0e3-muted hidden lg:table-cell whitespace-nowrap">
                  {formatFecha(fila.ultimoPago)}
                </td>
                <td className="px-4 py-3">
                  <CuentaCorrienteEstadoBadge fila={fila} estado={fila.estado} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Link to={`/clientes/${fila.id}/cuenta-corriente`} title="Ver cuenta corriente">
                      <Button color="secondary" size="sm" icon={<FaEye />}>
                        <span className="hidden sm:inline">Ver</span>
                      </Button>
                    </Link>
                    {fila.saldo > 0 ? (
                      <Button
                        color="primary"
                        size="sm"
                        icon={<FaMoneyBillWave />}
                        title="Registrar pago"
                        disabled={pagoCargando}
                        onClick={() => onRegistrarPago?.(fila)}
                      >
                        <span className="hidden sm:inline">{pagoCargando ? '…' : 'Pago'}</span>
                      </Button>
                    ) : null}
                    <Link
                      to={`/punto-venta?cliente=${encodeURIComponent(fila.id)}`}
                      state={{
                        clientePreseleccionado: {
                          id: fila.id,
                          nombre: fila.nombre,
                          telefono: fila.telefono,
                          dni_cuit: fila.dni_cuit
                        }
                      }}
                      title="Nueva venta"
                    >
                      <Button color="secondary" size="sm" icon={<FaShoppingCart />}>
                        <span className="hidden sm:inline">Venta</span>
                      </Button>
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CuentaCorrienteTabla;
